import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { EventWordmark } from '../EventWordmark';
import { EnterButton } from './EnterButton';
import { SceneCaption } from './SceneCaption';
import { createVentureScene, VentureTargets } from '../../utils/ventureScene';
import { useVentureTravel } from '../../hooks/useVentureTravel';
import { ventureGate, ventureSpaceScenes } from '../../data/velaris';
type Phase = 'gate' | 'entering' | 'travel' | 'leaving';
type VentureGateProps = {
  onLeave: () => void; // page content may appear underneath
  onDone: () => void; // the gate has fully faded away
};
const ease = [0.22, 1, 0.36, 1] as const;
const total = ventureSpaceScenes.length;
export function VentureGate({
  onLeave,
  onDone
}: VentureGateProps) {
  const reduce = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const targets = useRef<VentureTargets>({
    pointerX: 0,
    pointerY: 0,
    dragYaw: 0,
    dragPitch: 0,
    entered: 0,
    progress: 0,
    leaving: 0
  });
  const [phase, setPhase] = useState<Phase>('gate');
  const phaseRef = useRef<Phase>('gate');
  const [webglFailed, setWebglFailed] = useState(false);
  const [dragged, setDragged] = useState(false);

  // Cursor, used for the tilting title card and the drifting captions
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, {
    stiffness: 60,
    damping: 18
  });
  const sy = useSpring(my, {
    stiffness: 60,
    damping: 18
  });
  const rotateY = useTransform(sx, [-1, 1], [-9, 9]);
  const rotateX = useTransform(sy, [-1, 1], [7, -7]);
  const go = (next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  };
  const leave = useCallback(() => {
    if (phaseRef.current === 'leaving') return;
    targets.current.leaving = 1;
    go('leaving');
    onLeave();
  }, [onLeave]);
  const enter = useCallback(() => {
    if (phaseRef.current !== 'gate') return;
    targets.current.entered = 1;
    go('entering');
    window.setTimeout(() => {
      if (phaseRef.current !== 'entering') return;
      if (reduce) leave();else go('travel');
    }, 1500);
  }, [reduce, leave]);
  const progress = useVentureTravel(phase === 'travel', leave);
  // Captions change hands midway through each crossfade, as the new environment takes over
  const sceneIndex = Math.min(total - 1, Math.floor(progress * total + 0.2));
  const scene = ventureSpaceScenes[sceneIndex];

  // Progress within the current environment, smoothed for the caption drift
  const localRaw = useMotionValue(0);
  const local = useSpring(localRaw, {
    stiffness: 50,
    damping: 20
  });
  // The progress line eases toward the journey rather than jumping with each wheel tick
  const barRaw = useMotionValue(0);
  const bar = useSpring(barRaw, {
    stiffness: 22,
    damping: 20,
    mass: 1
  });
  useEffect(() => {
    targets.current.progress = progress;
    localRaw.set(Math.max(0, Math.min(1, progress * total + 0.2 - sceneIndex)));
    barRaw.set(progress);
  }, [progress, sceneIndex, localRaw, barRaw]);

  // Scene
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const scene3d = createVentureScene(canvas, targets.current, {
        gate: {
          image: ventureGate.gateImage,
          fallback: ventureGate.gateFallback
        },
        scenes: ventureSpaceScenes
      });
      return () => scene3d.dispose();
    } catch {
      setWebglFailed(true);
    }
  }, []);

  // Hover to lean, drag to turn around freely
  useEffect(() => {
    let down: {
      x: number;
      y: number;
      yaw: number;
      pitch: number;
    } | null = null;
    const onDown = (e: PointerEvent) => {
      down = {
        x: e.clientX,
        y: e.clientY,
        yaw: targets.current.dragYaw,
        pitch: targets.current.dragPitch
      };
    };
    const onMove = (e: PointerEvent) => {
      const x = e.clientX / window.innerWidth * 2 - 1;
      const y = e.clientY / window.innerHeight * 2 - 1;
      if (!reduce) {
        targets.current.pointerX = x;
        targets.current.pointerY = y;
        mx.set(x);
        my.set(y);
      }
      if (down) {
        const dx = e.clientX - down.x;
        const dy = e.clientY - down.y;
        targets.current.dragYaw = down.yaw + dx * 0.004;
        if (e.pointerType === 'mouse') targets.current.dragPitch = Math.max(-0.5, Math.min(0.5, down.pitch + dy * 0.003));
        if (Math.abs(dx) > 8) setDragged(true);
      }
    };
    const onUp = () => {
      down = null;
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [reduce, mx, my]);

  // Hold the page still until the gate hands over
  useEffect(() => {
    if (phase === 'leaving') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [phase]);
  return <motion.div role="dialog" aria-modal="true" aria-label="Venturist — enter the experience" className="fixed inset-0 z-[90] cursor-grab touch-none overflow-hidden bg-[#050505] text-paper active:cursor-grabbing" initial={{
    opacity: 1
  }} animate={{
    opacity: phase === 'leaving' ? 0 : 1
  }} transition={{
    duration: 1.6,
    ease,
    delay: phase === 'leaving' ? 0.9 : 0
  }} onAnimationComplete={() => {
    if (phaseRef.current === 'leaving') onDone();
  }} data-id="element-1332" data-mp="z5mhdg12v5f5">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-1333" data-mp="l5lho3wju419" />
      {webglFailed && <div className="absolute inset-0 bg-[#050505]" aria-hidden="true" data-id="element-1334" data-mp="2mvkbt97p0r93" />}
      {/* Cinematic grade: vignette, a low shade for legible captions, light grain */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_45%,rgba(0,0,0,0.7))]" aria-hidden="true" data-id="element-1335" data-mp="2i27nl06xemgv" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-[linear-gradient(to_top,rgba(0,0,0,0.7),rgba(0,0,0,0))]" aria-hidden="true" data-id="element-1336" data-mp="294oynkq5vnzp" />
      <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.06] motion-reduce:hidden" aria-hidden="true" data-id="element-1337" data-mp="31sbbzkp6y6zr" />

      {/* Top bar */}
      <motion.div className="absolute inset-x-0 top-0 z-[2] flex items-center justify-between px-6 py-6 md:px-16" initial={{
      opacity: 0
    }} animate={{
      opacity: phase === 'leaving' ? 0 : 1
    }} transition={{
      duration: 1.2,
      ease,
      delay: phase === 'gate' ? 0.8 : 0
    }} data-id="element-1338" data-mp="2wrnau67909rt">
        <Link to="/" className="inline-flex items-center gap-2 text-[14px] font-light text-paper/75 transition-colors duration-150 hover:text-paper" data-id="element-1339" data-mp="1gddnnfeqsr2k">
          <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.25} aria-hidden="true" data-id="element-1340" data-mp="1b9uy5lwffabc" />
          Superluxury
        </Link>
        <span className="hidden font-logo text-[18px] tracking-[0.3em] text-paper/85 md:block" data-id="element-1341" data-mp="1g8xxqqtn0obx">THE VENTURIST CLUB</span>
        <button type="button" onClick={leave} className="text-[14px] font-light text-paper/75 transition-colors duration-150 hover:text-paper" data-id="element-1342" data-mp="1l0pksrdl4bxb">
          Skip intro
        </button>
      </motion.div>

      {/* The gate */}
      <AnimatePresence data-id="element-1343" data-mp="1u2p45qc2ns3f">
        {phase === 'gate' && <motion.div key="gate" className="absolute inset-0 z-[1] flex items-center justify-center px-6 [perspective:1200px]" exit={{
        opacity: 0,
        scale: reduce ? 1 : 1.14,
        filter: reduce ? 'blur(0px)' : 'blur(14px)'
      }} transition={{
        duration: 1,
        ease
      }} data-id="element-1344" data-mp="1z0qnmcdettff">
            <motion.div style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d'
        }} className="flex flex-col items-center text-center" data-id="element-1345" data-mp="9larpyohy47h">
              <motion.p initial={{
            opacity: 0,
            y: reduce ? 0 : 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.4,
            ease,
            delay: 0.3
          }} className="text-[14px] font-light tracking-[0.02em] text-paper/75" style={{
            transform: 'translateZ(40px)'
          }} data-id="element-1346" data-mp="2bfyj1mjruy1q">
                {ventureGate.kicker}
              </motion.p>
              <motion.h1 initial={{
            opacity: 0,
            y: reduce ? 0 : 20,
            filter: reduce ? 'blur(0px)' : 'blur(12px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} transition={{
            duration: 2,
            ease,
            delay: 0.5
          }} className="mt-4" style={{
            transform: 'translateZ(90px)'
          }} data-id="element-1347" data-mp="1nkm0bfwsy3aa">
                <span className="sr-only" data-id="element-1348" data-mp="3d1daincseinl">Venturist</span>
                <EventWordmark name="Venturist" className="text-[84px] md:text-[144px] lg:text-[176px]" data-id="element-1349" data-mp="38wngl41ove5b" />
              </motion.h1>
              <motion.div initial={{
            opacity: 0,
            y: reduce ? 0 : 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.3,
            ease,
            delay: 1.3
          }} className="mt-12 flex flex-col items-center gap-4" style={{
            transform: 'translateZ(60px)'
          }} data-id="element-1350" data-mp="3gfwostygl6ss">
                <EnterButton label={ventureGate.enterLabel} onEnter={enter} data-id="element-1351" data-mp="32l4t48vq8b4" />
                <span className="text-[13px] font-light text-paper/60" data-id="element-1352" data-mp="34nz40h2enln">{ventureGate.hint}</span>
              </motion.div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Story captions over the 360° environments */}
      <AnimatePresence data-id="element-1353" data-mp="3ufo7h7zx7bd4">
        {phase === 'travel' && <motion.div key="travel" className="pointer-events-none absolute inset-0 z-[1]" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-1354" data-mp="m1fmom3n3ovi">
            <AnimatePresence mode="wait" data-id="element-1355" data-mp="1en69kqm2ozj3">
              <SceneCaption key={sceneIndex} scene={scene} index={sceneIndex} total={total} local={local} sx={sx} sy={sy} data-id="element-1356" data-mp="1em4zaocvsh2f" />
            </AnimatePresence>

            <p className="sr-only" aria-live="polite" data-id="element-1357" data-mp="15mhv5usrox5o">
              {scene.time}, {scene.place}. {scene.title} {scene.line}
            </p>

            {/* A single hairline that glides with the journey */}
            <div className="absolute inset-x-0 bottom-5 flex flex-col items-center gap-3" data-id="element-1358" data-mp="vo23nqptvlvn">
              <AnimatePresence data-id="element-1359" data-mp="hwvz3g7oyy1w">
                {progress < 0.02 && <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.6,
              ease
            }} className="whitespace-nowrap text-[11px] font-light text-paper/45" data-id="element-1360" data-mp="hyqgwlno2a1r">
                    {dragged ? 'Scroll to continue' : 'Scroll · drag to look around'}
                  </motion.span>}
              </AnimatePresence>
              <span className="relative h-px w-[160px] overflow-hidden bg-paper/10 md:w-[220px]" data-id="element-1361" data-mp="3lhmoziwmzjc5">
                <motion.span style={{
              scaleX: bar
            }} className="absolute inset-0 origin-left bg-paper/40" data-id="element-1362" data-mp="3bi2lbz29olk" />
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Hand-over: the light blooms, then the page appears */}
      {phase === 'leaving' && <motion.div className="pointer-events-none absolute inset-0 z-[3] bg-[#f1e4cc]" initial={{
      opacity: 0
    }} animate={{
      opacity: reduce ? 0 : [0, 0.6, 0]
    }} transition={{
      duration: 2,
      ease,
      times: [0, 0.45, 1]
    }} aria-hidden="true" data-id="element-1363" data-mp="891h8czo83e1" />}
    </motion.div>;
}